import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, FileText, Plus, Headphones, Trash2, Edit, ChevronUp, ChevronDown, PanelRightClose, PanelRightOpen, Eye, RefreshCw, Wand2 } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { Input } from '@/components/atoms/Input';
import { Select } from '@/components/atoms/Select';
import { Badge } from '@/components/atoms/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/molecules/Dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/molecules/Tabs';
import { useStory, useActivities, useResources, useSaveStory } from '@/hooks/useBackofficeData';
import { useCategoryLabels } from '@/hooks/useCategoryLabels';
import { regenerateStoryAudio, generateStoryBackgroundImage, migrateLegacyStory } from '@/services/api/backoffice.api';
import { ROUTES, buildRoute } from '@/constants/routes';
import type { EnglishLevel } from '@/types/story.types';
import type {
  BackgroundImageVersion,
  StoryFormData,
  StoryPublishStatus as BackofficeStoryStatus,
  StoryType,
} from '@/types/backoffice.types';
import styles from './StoryEditPage.module.scss';

const RESOURCE_TYPE_LABELS: Record<string, string> = {
  text: 'Texto',
  audio: 'Audio',
  video: 'Vídeo',
};

export function StoryEditPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new' || id === 'create';

  const { story, isLoading } = useStory(isNew ? undefined : id);
  const { activities, deleteActivity, reorderActivity } = useActivities(isNew ? undefined : id);
  const { resources, deleteResource, refetchResources } = useResources(isNew ? undefined : id);
  const { saveStory, isSaving } = useSaveStory();
  const { categoryOptions, labelFor } = useCategoryLabels();

  // Determine initial type: from URL param (new story) or from loaded story (edit)
  const typeFromUrl = (searchParams.get('type') as StoryType | null) ?? 'general';

  // Form state
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [titleTranslation, setTitleTranslation] = useState<string>('');
  const [descriptionTranslation, setDescriptionTranslation] = useState<string>('');
  const [level, setLevel] = useState<EnglishLevel>('B1');
  const [category, setCategory] = useState<string>('');
  const [type, setType] = useState<StoryType>(typeFromUrl);
  const [status, setStatus] = useState<BackofficeStoryStatus>('published');
  const [estimatedTime, setEstimatedTime] = useState<number>(30);
  const [xpReward, setXpReward] = useState<number>(100);
  const [backgroundImage, setBackgroundImage] = useState<string>('');
  const [backgroundImageHistory, setBackgroundImageHistory] = useState<BackgroundImageVersion[]>([]);
  const [imagePreviewError, setImagePreviewError] = useState<boolean>(false);

  // Update form state when story loads (edit mode)
  useEffect(() => {
    if (story && !isNew) {
      setTitle(story.title || '');
      setDescription(story.description || '');
      setTitleTranslation(story.titleTranslation || '');
      setDescriptionTranslation(story.descriptionTranslation || '');
      setLevel(story.level || 'B1');
      setCategory(story.category || '');
      setType(story.type || 'general');
      setStatus(story.status || 'published');
      setEstimatedTime(story.estimatedTime || 30);
      setXpReward(story.xpReward || 100);
      setBackgroundImage(story.backgroundImage || '');
      setBackgroundImageHistory(story.backgroundImageHistory ?? []);
    }
  }, [story, isNew]);

  // Reset preview error whenever the image URL changes (lets a new URL retry loading)
  useEffect(() => {
    setImagePreviewError(false);
  }, [backgroundImage]);

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Por favor, introduce un título para la historia');
      return;
    }

    // Listening validation: warn if trying to publish without audio resources
    if (type === 'listening' && status === 'published' && !isNew && resources.length === 0) {
      const proceed = window.confirm(
        'Las historias de escucha requieren al menos un recurso de audio con transcript. ' +
          'Esta historia no tiene recursos todavía. ¿Quieres guardarla como Borrador?'
      );
      if (!proceed) return;
      setStatus('draft');
    }

    try {
      const storyData: StoryFormData = {
        title: title.trim(),
        description: description.trim(),
        titleTranslation: titleTranslation.trim(),
        descriptionTranslation: descriptionTranslation.trim(),
        level,
        category: category.trim(),
        type,
        status,
        estimatedTime,
        xpReward,
        backgroundImage: backgroundImage.trim() || undefined,
      };

      const result = await saveStory(isNew ? null : id, storyData);

      if (isNew && result) {
        navigate(buildRoute(ROUTES.BACKOFFICE.STORY_EDIT, { id: result.id }), { replace: true });
      } else {
        alert('¡Historia guardada correctamente!');
      }
    } catch (error) {
      console.error('Failed to save story:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      alert(`Error al guardar la historia: ${errorMessage}`);
    }
  };

  const [isRegeneratingAudio, setIsRegeneratingAudio] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [isGeneratingBackgroundImage, setIsGeneratingBackgroundImage] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [showMigrateDialog, setShowMigrateDialog] = useState(false);
  const hasAudioScript = resources.some((r) => r.title === 'Audio Script');

  const handleOpenRegenerateDialog = () => setShowRegenerateDialog(true);
  const handleCloseRegenerateDialog = () => setShowRegenerateDialog(false);

  const handleRegenerateAudio = async () => {
    if (!id || isNew || !hasAudioScript) return;
    try {
      setIsRegeneratingAudio(true);
      handleCloseRegenerateDialog();
      await regenerateStoryAudio(id);
      await refetchResources();
      alert('Audio regenerado correctamente');
    } catch (err) {
      console.error('Regenerate audio error:', err);
      const msg = err instanceof Error ? err.message : 'Error al regenerar el audio';
      alert(msg);
    } finally {
      setIsRegeneratingAudio(false);
    }
  };

  const handleMigrateStory = async () => {
    if (!id || isNew) return;
    try {
      setIsMigrating(true);
      setShowMigrateDialog(false);
      const report = await migrateLegacyStory(id);

      const repaired = report.activityRepairs.filter((r) => r.repaired).length;
      const skipped = report.activityRepairs.filter((r) => !r.repaired);
      const retried = report.activityRepairs.filter((r) => r.attempts >= 2).length;
      const withResiduals = report.activityRepairs.filter((r) => (r.residualIssues?.length ?? 0) > 0);
      const lines: string[] = [];
      lines.push(`Migración de "${report.storyTitle}" completada en ${Math.round(report.durationMs / 100) / 10}s.`);
      lines.push('');
      lines.push(`Actividades de práctica: ${report.auditSummary.practiceActivities}`);
      lines.push(`Detectadas con issues: ${report.auditSummary.needingRepair}`);
      lines.push(`Reparadas correctamente: ${repaired}`);
      if (retried > 0) {
        lines.push(`Con segunda pasada correctiva: ${retried}`);
      }
      if (withResiduals.length > 0) {
        lines.push(`Con issues residuales tras 2 pasadas: ${withResiduals.length}`);
      }
      if (skipped.length > 0) {
        lines.push(`No reparadas (revisa logs): ${skipped.length}`);
      }
      if (report.audioScript.wasMalformed) {
        lines.push('');
        lines.push(`Audio script reformateado: ${report.audioScript.reformatted ? 'sí' : 'no'}`);
        lines.push(`Audio regenerado en ElevenLabs: ${report.audioScript.audioRegenerated ? 'sí' : 'no'}`);
        if (report.audioScript.note) lines.push(report.audioScript.note);
      }
      if (!report.storyUpdated) {
        lines.push('');
        lines.push('La historia ya cumple con el formato actual. No se ha modificado nada.');
      }
      alert(lines.join('\n'));

      if (report.storyUpdated) {
        await refetchResources();
        // Recargar la página para que activities recargue del backend con los cambios.
        window.location.reload();
      }
    } catch (err) {
      console.error('Migrate story error:', err);
      const msg = err instanceof Error ? err.message : 'Error al migrar la historia';
      alert(msg);
    } finally {
      setIsMigrating(false);
    }
  };

  const handleGenerateBackgroundImage = async () => {
    if (!id || isNew) return;
    try {
      setIsGeneratingBackgroundImage(true);
      const { imageUrl, history } = await generateStoryBackgroundImage(id);
      setBackgroundImage(imageUrl);
      setBackgroundImageHistory(history);
      alert('Imagen generada correctamente');
    } catch (err) {
      console.error('Generate background image error:', err);
      const msg = err instanceof Error ? err.message : 'Error al generar la imagen';
      alert(msg);
    } finally {
      setIsGeneratingBackgroundImage(false);
    }
  };

  const handleAddResource = () => {
    if (!id || isNew) {
      alert('Guarda la historia primero antes de añadir contenido');
      return;
    }
    navigate(buildRoute(ROUTES.BACKOFFICE.STORY_RESOURCE_CREATE, { storyId: id }));
  };

  const handleEditResource = (resourceId: string) => {
    if (!id || isNew) return;
    navigate(buildRoute(ROUTES.BACKOFFICE.STORY_RESOURCE_EDIT, { storyId: id, resourceId }));
  };

  const handleDeleteResource = async (resourceId: string) => {
    if (!window.confirm('¿Seguro que quieres eliminar este contenido?')) return;
    try {
      await deleteResource(resourceId);
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Error al eliminar el contenido');
    }
  };

  const handleAddActivity = () => {
    if (!id || isNew) {
      alert('Guarda la historia primero antes de añadir actividades');
      return;
    }
    navigate(buildRoute(ROUTES.BACKOFFICE.ACTIVITY_CREATE, { storyId: id }));
  };

  const handleEditActivity = (activityId: string) => {
    if (!id || isNew) return;
    navigate(buildRoute(ROUTES.BACKOFFICE.ACTIVITY_EDIT, { storyId: id, activityId }));
  };

  const handleDeleteActivity = async (activityId: string) => {
    if (!window.confirm('¿Seguro que quieres eliminar esta actividad?')) return;
    try {
      await deleteActivity(activityId);
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Error al eliminar la actividad');
    }
  };

  const handleMoveActivityUp = async (activity: { id: string; order: number }, index: number) => {
    if (index === 0) return;
    try {
      await reorderActivity(activity.id, index - 1);
    } catch (error) {
      console.error('Error reordering activity:', error);
      alert('Error al reordenar la actividad');
    }
  };

  const handleMoveActivityDown = async (activity: { id: string; order: number }, index: number) => {
    if (index === activities.length - 1) return;
    try {
      await reorderActivity(activity.id, index + 1);
    } catch (error) {
      console.error('Error reordering activity:', error);
      alert('Error al reordenar la actividad');
    }
  };

  const getActivityTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      multiple_choice: 'Opción múltiple',
      fill_in_blank: 'Completar huecos',
      fill_in_blank_tap: 'Completar huecos (tap)',
      true_false: 'Verdadero/Falso',
      matching: 'Emparejar',
      ordering: 'Ordenar',
      open_ended: 'Respuesta abierta',
    };
    return labels[type] || type;
  };

  const isListening = type === 'listening';
  const tabFromUrl = searchParams.get('tab') as 'resources' | 'activities' | null;
  const validTab = tabFromUrl && ['resources', 'activities'].includes(tabFromUrl)
    ? tabFromUrl
    : 'resources';
  const [activeTab, setActiveTab] = useState<'resources' | 'activities'>(validTab);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab && ['resources', 'activities'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  {/* eslint-disable-next-line react-hooks/exhaustive-deps */}
  }, [tabFromUrl]);

  if (isLoading && !isNew) {
    return (
      <div className={styles.storyEdit}>
        <h1>Cargando...</h1>
      </div>
    );
  }

  return (
    <div className={styles.storyEdit}>
      <Tabs
        className={styles.storyEdit__tabs}
        value={activeTab}
        onValueChange={(v) => {
          const tab = v as 'resources' | 'activities';
          setActiveTab(tab);
          if (id && !isNew) {
            const url = buildRoute(ROUTES.BACKOFFICE.STORY_EDIT, { id });
            navigate(`${url}?tab=${tab}`, { replace: true });
          }
        }}
      >
        <div className={styles.storyEdit__toolbar}>
          <div className={styles.storyEdit__header}>
            <IconButton
              icon={ArrowLeft}
              onClick={() => navigate(ROUTES.BACKOFFICE.STORIES)}
              aria-label="Volver a Historias"
            />
            <input
              type="text"
              className={styles.storyEdit__titleInput}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={isNew ? 'Crear historia' : 'Añadir título'}
              aria-label="Título de la historia"
            />
            <div className={styles.storyEdit__headerActions}>
              {!isNew && id && (
                <IconButton
                  icon={Eye}
                  onClick={() => window.open(buildRoute(ROUTES.PREVIEW.STORY, { id }), '_blank')}
                  aria-label="Previsualizar como estudiante"
                />
              )}
              <IconButton
                icon={Save}
                variant="primary"
                onClick={handleSave}
                disabled={isSaving}
                aria-label={isSaving ? 'Guardando...' : 'Guardar historia'}
              />
              <IconButton
                icon={isSidebarOpen ? PanelRightClose : PanelRightOpen}
                onClick={() => setIsSidebarOpen((v) => !v)}
                aria-label={isSidebarOpen ? 'Ocultar panel de información' : 'Mostrar panel de información'}
              />
            </div>
          </div>
        </div>

        <div className={styles.storyEdit__body} data-sidebar-open={isSidebarOpen}>
          <div className={styles.storyEdit__main}>
            <TabsList className={styles.storyEdit__tabsList}>
              <TabsTrigger value="resources">
                Contenido
                {!isNew && resources.length > 0 && (
                  <span className={styles.storyEdit__tabBadge}>{resources.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="activities">
                Actividades
                {!isNew && activities.length > 0 && (
                  <span className={styles.storyEdit__tabBadge}>{activities.length}</span>
                )}
              </TabsTrigger>
            </TabsList>

        <TabsContent value="resources">
          {isNew ? (
            <div className={styles.storyEdit__notice}>
              {isListening ? <Headphones size={24} /> : <FileText size={24} />}
              <p>
                <strong>Nota:</strong> Guarda esta historia primero para añadir contenido.
                {isListening && (
                  <> Las historias de escucha requieren al menos un recurso de audio con transcript.</>
                )}
              </p>
            </div>
          ) : (
            <div className={styles.storyEdit__tabPanel}>
              <div className={styles.storyEdit__activitiesHeader}>
                <div>
                  <h2 className={styles.storyEdit__sectionTitle}>Contenido</h2>
                  <p className={styles.storyEdit__activitiesSubtitle}>
                    {isListening
                      ? 'Contenido de audio con transcript (obligatorio para publicar)'
                      : 'Materiales de aprendizaje: texto, audio y vídeo'}
                  </p>
                </div>
<div className={styles.storyEdit__headerActions}>
                {!isNew && (
                  <>
                    <Button
                      variant="secondary"
                      leftIcon={<Wand2 size={16} className={isMigrating ? styles.storyEdit__spin : undefined} />}
                      onClick={() => setShowMigrateDialog(true)}
                      disabled={isMigrating}
                    >
                      {isMigrating ? 'Migrando…' : 'Migrar a formato actual'}
                    </Button>
                    <Dialog open={showMigrateDialog} onOpenChange={(open) => !open && setShowMigrateDialog(false)}>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Migrar historia legacy</DialogTitle>
                          <DialogDescription>
                            Revisa toda la historia y arregla lo que no cumpla con el formato actual: feedback sin traducción, referencias a posición, respuestas reveladas en el explanation, success_message residuales, y audio script sin saltos de párrafo. Si el script cambia, el audio se regenera en ElevenLabs automáticamente. Puede tardar entre 30 y 120 segundos según el número de actividades.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="secondary" onClick={() => setShowMigrateDialog(false)}>
                            Cancelar
                          </Button>
                          <Button onClick={handleMigrateStory} disabled={isMigrating}>
                            Migrar
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
                {hasAudioScript && (
                  <>
                    <Button
                      variant="secondary"
                      leftIcon={<RefreshCw size={16} className={isRegeneratingAudio ? styles.storyEdit__spin : undefined} />}
                      onClick={handleOpenRegenerateDialog}
                      disabled={isRegeneratingAudio}
                    >
                      {isRegeneratingAudio ? 'Regenerando…' : 'Regenerar audio'}
                    </Button>
                    <Dialog open={showRegenerateDialog} onOpenChange={(open) => !open && handleCloseRegenerateDialog()}>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Regenerar audio</DialogTitle>
                          <DialogDescription>
                            Se regenerará el audio usando el script guardado o reconstruir desde el transcript (con saltos de línea inferidos por pausas).
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <Button variant="secondary" onClick={handleCloseRegenerateDialog}>
                            Cancelar
                          </Button>
                          <Button onClick={handleRegenerateAudio} disabled={isRegeneratingAudio}>
                            Regenerar
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
                <Button variant="secondary" onClick={handleAddResource}>
                  Añadir contenido
                </Button>
              </div>
              </div>
              <div className={styles.storyEdit__activitiesContent}>
              {resources.length === 0 ? (
                <div className={styles.storyEdit__emptyState}>
                  {isListening ? (
                    <Headphones size={48} className={styles.storyEdit__emptyIcon} />
                  ) : (
                    <FileText size={48} className={styles.storyEdit__emptyIcon} />
                  )}
                  <h3 className={styles.storyEdit__emptyTitle}>Sin contenido</h3>
                  <p className={styles.storyEdit__emptyDescription}>
                    {isListening
                      ? 'Añade contenido de audio con transcript para que esta historia de escucha sea válida'
                      : 'Añade contenido de texto, audio o vídeo para que los estudiantes aprendan'}
                  </p>
                  <Button
                    leftIcon={<Plus />}
                    onClick={handleAddResource}
                    className={styles.storyEdit__addButton}
                  >
                    Añadir primer contenido
                  </Button>
                </div>
              ) : (
                <div className={styles.storyEdit__resourcesList}>
                    {resources.map((resource) => (
                    <Card
                      key={resource.id}
                      className={styles.storyEdit__resourceItem}
                      onClick={() => handleEditResource(resource.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleEditResource(resource.id);
                        }
                      }}
                    >
                      <CardHeader className={styles.storyEdit__resourceItemHeader}>
                        <div className={styles.storyEdit__resourceItemInfo}>
                          <Badge variant="default" className={styles.storyEdit__cardBadge}>
                            {RESOURCE_TYPE_LABELS[resource.type]}
                          </Badge>
                          <CardTitle className={styles.storyEdit__cardItemTitle}>{resource.title}</CardTitle>
                        </div>
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                        <div
                          className={styles.storyEdit__resourceItemActions}
                          onClick={(e) => e.stopPropagation()}
                          role="group"
                          aria-label="Acciones"
                        >
                          <IconButton
                            icon={Edit}
                            onClick={() => handleEditResource(resource.id)}
                            aria-label="Editar contenido"
                          />
                          <IconButton
                            icon={Trash2}
                            onClick={() => handleDeleteResource(resource.id)}
                            aria-label="Eliminar contenido"
                          />
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="activities">
          {isNew ? (
            <div className={styles.storyEdit__notice}>
              <FileText size={24} />
              <p>
                <strong>Nota:</strong> Guarda esta historia primero para añadir actividades.
              </p>
            </div>
          ) : (
            <div className={styles.storyEdit__tabPanel}>
              <div className={styles.storyEdit__activitiesHeader}>
                <div>
                  <h2 className={styles.storyEdit__sectionTitle}>Actividades</h2>
                  <p className={styles.storyEdit__activitiesSubtitle}>
                    Gestiona las actividades y ejercicios de la historia
                  </p>
                </div>
<Button variant="secondary" onClick={handleAddActivity}>
                Añadir actividad
              </Button>
              </div>
              <div className={styles.storyEdit__activitiesContent}>
              {activities.length === 0 ? (
                <div className={styles.storyEdit__emptyState}>
                  <FileText size={48} className={styles.storyEdit__emptyIcon} />
                  <h3 className={styles.storyEdit__emptyTitle}>Sin actividades</h3>
                  <p className={styles.storyEdit__emptyDescription}>
                    Añade actividades para hacer esta historia interactiva y atractiva para los estudiantes
                  </p>
                  <Button
                    leftIcon={<Plus />}
                    onClick={handleAddActivity}
                    className={styles.storyEdit__addButton}
                  >
                    Añadir primera actividad
                  </Button>
                </div>
              ) : (
                <div className={styles.storyEdit__activitiesList}>
                    {activities.map((activity, index) => (
                    <Card
                      key={activity.id}
                      className={styles.storyEdit__activityItem}
                      onClick={() => handleEditActivity(activity.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleEditActivity(activity.id);
                        }
                      }}
                    >
                      <CardHeader className={styles.storyEdit__activityItemHeader}>
                        <div className={styles.storyEdit__activityItemInfo}>
                          <Badge variant="default" className={styles.storyEdit__cardBadge}>{getActivityTypeLabel(activity.type)}</Badge>
                          <CardTitle className={styles.storyEdit__cardItemTitle}>{activity.question}</CardTitle>
                        </div>
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                        <div
                          className={styles.storyEdit__activityItemActions}
                          onClick={(e) => e.stopPropagation()}
                          role="group"
                          aria-label="Acciones"
                        >
                          <div className={styles.storyEdit__activityItemReorder}>
                            <IconButton
                              icon={ChevronUp}
                              onClick={() => handleMoveActivityUp(activity, index)}
                              disabled={index === 0}
                              aria-label="Subir"
                            />
                            <IconButton
                              icon={ChevronDown}
                              onClick={() => handleMoveActivityDown(activity, index)}
                              disabled={index === activities.length - 1}
                              aria-label="Bajar"
                            />
                          </div>
                          <IconButton
                            icon={Edit}
                            onClick={() => handleEditActivity(activity.id)}
                            aria-label="Editar actividad"
                          />
                          <IconButton
                            icon={Trash2}
                            onClick={() => handleDeleteActivity(activity.id)}
                            aria-label="Eliminar actividad"
                          />
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              )}
              </div>
            </div>
          )}
        </TabsContent>
          </div>

          {isSidebarOpen && (
          <aside className={styles.storyEdit__sidebar}>
            <Card>
              <CardHeader>
                <CardTitle>Información de la historia</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={styles.storyEdit__form}>
                  <div className={styles.storyEdit__formGroup}>
                    <label htmlFor="title">Título (EN)</label>
                    <Input
                      id="title"
                      name="title"
                      type="text"
                      placeholder="Título de la historia"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>

                  <div className={styles.storyEdit__formGroup}>
                    <label htmlFor="titleTranslation">Título (ES)</label>
                    <Input
                      id="titleTranslation"
                      name="titleTranslation"
                      type="text"
                      placeholder="Traducción del título al español"
                      value={titleTranslation}
                      onChange={(e) => setTitleTranslation(e.target.value)}
                    />
                  </div>

                  <div className={styles.storyEdit__formGroup}>
                    <label htmlFor="description">Descripción (EN)</label>
                    <textarea
                      id="description"
                      name="description"
                      placeholder="Descripción de la historia"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className={styles.storyEdit__textarea}
                    />
                  </div>

                  <div className={styles.storyEdit__formGroup}>
                    <label htmlFor="descriptionTranslation">Descripción (ES)</label>
                    <textarea
                      id="descriptionTranslation"
                      name="descriptionTranslation"
                      placeholder="Traducción de la descripción al español"
                      value={descriptionTranslation}
                      onChange={(e) => setDescriptionTranslation(e.target.value)}
                      rows={4}
                      className={styles.storyEdit__textarea}
                    />
                    <p className={styles.storyEdit__formHint}>
                      Título (ES) y Descripción (ES) son lo que ven los estudiantes con la
                      interfaz en español. Si se dejan vacíos, se muestra la versión en inglés.
                    </p>
                  </div>

                  <div className={styles.storyEdit__formGroup}>
                    <label htmlFor="level">Nivel</label>
                    <Select
                      value={level}
                      onValueChange={(value) => setLevel(value as EnglishLevel)}
                      options={[
                        { label: 'A1', value: 'A1' },
                        { label: 'A2', value: 'A2' },
                        { label: 'B1', value: 'B1' },
                        { label: 'B2', value: 'B2' },
                        { label: 'C1', value: 'C1' },
                        { label: 'C2', value: 'C2' },
                      ]}
                    />
                  </div>

                  <div className={styles.storyEdit__formGroup}>
                    <label htmlFor="category">Categoría</label>
                    <Select
                      value={category}
                      onValueChange={(value) => setCategory(value)}
                      placeholder="Sin categoría"
                      options={
                        category && !categoryOptions.some((o) => o.value === category)
                          ? [{ label: labelFor(category), value: category }, ...categoryOptions]
                          : categoryOptions
                      }
                    />
                  </div>

                  <div className={styles.storyEdit__formGroup}>
                    <label htmlFor="status">Estado</label>
                    <Select
                      value={status}
                      onValueChange={(value) => setStatus(value as BackofficeStoryStatus)}
                      options={[
                        { label: 'Publicado', value: 'published' },
                        { label: 'Borrador', value: 'draft' },
                      ]}
                    />
                  </div>

                  <div className={styles.storyEdit__formGroup}>
                    <label htmlFor="estimatedTime">Tiempo estimado (minutos)</label>
                    <Input
                      id="estimatedTime"
                      name="estimatedTime"
                      type="number"
                      placeholder="30"
                      value={estimatedTime}
                      onChange={(e) => setEstimatedTime(parseInt(e.target.value) || 30)}
                      min="1"
                    />
                  </div>

                  <div className={styles.storyEdit__formGroup}>
                    <label htmlFor="xpReward">Recompensa XP</label>
                    <Input
                      id="xpReward"
                      name="xpReward"
                      type="number"
                      placeholder="100"
                      value={xpReward}
                      onChange={(e) => setXpReward(parseInt(e.target.value) || 100)}
                      min="1"
                    />
                  </div>

                  <div className={styles.storyEdit__formGroup}>
                    <label htmlFor="backgroundImage">Imagen de fondo (URL)</label>
                    <div className={styles.storyEdit__inlineInputActions}>
                      <Input
                        id="backgroundImage"
                        name="backgroundImage"
                        type="url"
                        placeholder="https://..."
                        value={backgroundImage}
                        onChange={(e) => setBackgroundImage(e.target.value)}
                      />
                      {!isNew && (
                        <Button
                          type="button"
                          variant="secondary"
                          leftIcon={
                            <RefreshCw
                              size={16}
                              className={isGeneratingBackgroundImage ? styles.storyEdit__spin : undefined}
                            />
                          }
                          onClick={handleGenerateBackgroundImage}
                          disabled={isGeneratingBackgroundImage}
                        >
                          {isGeneratingBackgroundImage
                            ? 'Generando…'
                            : backgroundImage.trim()
                              ? 'Regenerar imagen (IA)'
                              : 'Generar imagen (IA)'}
                        </Button>
                      )}
                    </div>
                    {backgroundImage.trim() && (
                      <div className={styles.storyEdit__imagePreview}>
                        {imagePreviewError ? (
                          <div className={styles.storyEdit__imagePreviewError}>
                            No se ha podido cargar la imagen desde esta URL.
                          </div>
                        ) : (
                          <img
                            src={backgroundImage}
                            alt="Vista previa de la imagen de fondo"
                            onError={() => setImagePreviewError(true)}
                          />
                        )}
                      </div>
                    )}
                    {backgroundImageHistory.length > 0 && (
                      <div className={styles.storyEdit__imageGallery}>
                        <span className={styles.storyEdit__imageGalleryLabel}>
                          Versiones generadas
                        </span>
                        <div className={styles.storyEdit__imageGalleryList}>
                          {backgroundImageHistory.map((entry) => {
                            const isActive = entry.url === backgroundImage;
                            return (
                              <button
                                key={`${entry.version}-${entry.url}`}
                                type="button"
                                className={[
                                  styles.storyEdit__imageGalleryItem,
                                  isActive
                                    ? styles['storyEdit__imageGalleryItem--active']
                                    : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                                onClick={() => setBackgroundImage(entry.url)}
                                aria-label={`Usar versión V${entry.version}`}
                                aria-pressed={isActive}
                              >
                                <img src={entry.url} alt={`Versión V${entry.version}`} />
                                <span className={styles.storyEdit__imageGalleryBadge}>
                                  V{entry.version}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </aside>
          )}
        </div>
      </Tabs>
    </div>
  );
}

export default StoryEditPage;
